// 🔧 leaderboard.js
// ✅ Purpose: Authorized event-results leaderboard with bounded result refreshes
// ✅ Features:
// - Enforces the event `resultsAudience` visibility policy before protected results are displayed
// - Supports Voters, Judges/Jury, Managers, and global Admin result audiences
// - Preserves historical `showLeaderboard` fallback behavior when `resultsAudience` is absent
// - Loads privacy-safe derived results through the trusted aggregate callable
// - Refreshes the aggregate projection every 20 seconds without overlapping requests
// - Cleans up the refresh timer when the page effect is replaced or unmounted
// - Supports People's Choice vote-count, Ranked Choice, and Judged Points result strategies
// - Displays detailed judged scorecard results with entry ranking and criterion averages
// - Displays normalized common-scale overall scorecard averages when available
// - Shows every evaluated Judged Points entry in the detailed dashboard regardless of leaderboardLimit
// - Displays all categories in alphabetical order where category-based results apply
// - Shows only entries with qualifying result data and applies `leaderboardLimit` from Firestore settings
// - Displays Best in Show when the active voting engine supports it and `bestInShowEnabled` is enabled
// - Redirects users who are not authorized for the configured results audience
// - Uses centralized voting-engine/result-strategy helpers instead of page-specific voting-type assumptions
// - Includes structured diagnostic logging for authorization, refresh lifecycle, loading, and aggregation
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  getDoc,
  doc,
} from "firebase/firestore";
import { Navigate } from "react-router-dom";
import { useEventContext } from "../../hooks/use_event_context";
import { db } from "../../firebase";
import { useAuth } from "../../auth_context";
import { useRole } from "../../role_context";
import { useCategories } from "../../hooks/use_categories";
import { useVotingStatus } from "../../hooks/use_voting_status";
import FullPageSpinner from "../../components/FullPageSpinner";
import AppPromoMessage from "../../components/AppPromoMessage";
import {
  getEventVotingType,
} from "../../utils/event_voting_type_helpers";
import { logger } from '../../utils/logger';
import {
  getLeaderboardAggregationMode,
} from "../../utils/vote_behavior_helpers";
import {
  getVotingTypeLabel,
  allowsBestInShow,
} from "../../utils/voting_engine_registry";
import {
  isVoteCountStrategy,
  isRankedEliminationStrategy,
  isAverageScoreStrategy,
} from "../../utils/result_strategy_helpers";
import SeoHead from "../../components/seo_head";
import { buildEventPageSeo } from "../../utils/event_page_seo_helpers";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";
import { loadEventResultProjection } from "../../services/event_results";

const Leaderboard = () => {

  const {
    eventslug,
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });

  const { user } = useAuth();
  const { categories } = useCategories(eventId);
  const {
    showLeaderboard,
    resolvedResultsAudience,
    loading: settingsLoading,
  } = useVotingStatus(eventId);
  const { role, eventRole, roleLoading } = useRole();
  const [carsWithVotes, setCarsWithVotes] = useState([]);
  const [rankedChoiceResults, setRankedChoiceResults] = useState([]);
  const [pointsResults, setPointsResults] = useState(null);
  const [bestInShowCars, setBestInShowCars] = useState([]);
  const [bestInShowEnabled, setBestInShowEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  // ✅ Resolve the current user's results-viewing authority before
  // attempting any protected /votes collection read.
  const normalizedRole = String(role || "").trim().toLowerCase();
  const normalizedEventRole = String(eventRole || "").trim().toLowerCase();

  const isAdmin = normalizedRole === "admin";

  // ✅ Mirror the event Manager boundary used by Firestore Rules.
  const isEventManager =
    isAdmin ||
    normalizedEventRole === "admin" ||
    normalizedEventRole === "manager" ||
    eventDoc?.ownerId === user?.uid ||
    (
      Array.isArray(eventDoc?.managerIds) &&
      eventDoc.managerIds.includes(user?.uid)
    );

  const isEventJudge = normalizedEventRole === "judge";

  const canViewResults =
    Boolean(user) &&
    (
      resolvedResultsAudience === "voters" ||
      (
        resolvedResultsAudience === "judges" &&
        (isEventJudge || isEventManager)
      ) ||
      (
        resolvedResultsAudience === "managers" &&
        isEventManager
      ) ||
      (
        resolvedResultsAudience === "admin" &&
        isAdmin
      )
    );
  const votingType = getEventVotingType(eventDoc);
  const aggregationMode = getLeaderboardAggregationMode(eventDoc);
  const votingTypeLabel = getVotingTypeLabel(votingType);
  const showBestInShow =
    bestInShowEnabled &&
    allowsBestInShow(votingType);  
  const isVoteCountLeaderboard =
    isVoteCountStrategy(votingType);
  const isRankedLeaderboard =
    isRankedEliminationStrategy(votingType);
  const isPointsLeaderboard =
    isAverageScoreStrategy(votingType);

  // 🐛 Debug current state
  useEffect(() => {
    logger.log("🏆 Leaderboard configuration", {
      votingType,
      aggregationMode,
      showLeaderboard,
    });
  }, [votingType, aggregationMode, showLeaderboard]);

  // 🔄 Load settings and public entry metadata once for this page state.
  // Refresh only the protected aggregate projection every 20 seconds.
  useEffect(() => {
    let cancelled = false;
    let refreshTimer = null;
    let requestInFlight = false;
    let failureLogged = false;
    let supportData = null;

    const reportRefreshFailure = (err) => {
      if (!failureLogged) {
        logger.error("❌ Aggregate leaderboard load failed:", { value: err });
        failureLogged = true;
        return;
      }

      // Repeated polling failures in the same episode stay console-only.
      logger.warn("Aggregate leaderboard refresh still unavailable.", {
        code: err?.code || "unknown",
      });
    };

    const refreshResults = async () => {
      if (requestInFlight || !supportData) return;
      requestInFlight = true;

      try {
        const resultView = await loadEventResultProjection({
          eventId,
          categories,
          entryMap: supportData.entryMap,
          leaderboardLimit: supportData.leaderboardLimit,
        });

        if (cancelled) return;

        setRankedChoiceResults(resultView.rankedChoiceResults);
        setPointsResults(resultView.pointsResults);
        setCarsWithVotes(resultView.carsWithVotes);
        setBestInShowCars(resultView.bestInShowCars);
        setLoading(false);
        failureLogged = false;
      } catch (err) {
        if (!cancelled) {
          reportRefreshFailure(err);
          setLoading(false);
        }
      } finally {
        requestInFlight = false;
      }
    };

    const initializeResults = async () => {
      try {
        setLoading(true);

        const [settingsDoc, carsSnap] = await Promise.all([
          getDoc(doc(db, "events", eventId, "settings", "voting")),
          getDocs(collection(db, "events", eventId, "cars")),
        ]);

        if (cancelled) return;

        const settings = settingsDoc.exists() ? settingsDoc.data() : {};
        const entryMap = {};

        carsSnap.docs.forEach((carDoc) => {
          entryMap[carDoc.id] = {
            ...carDoc.data(),
            cid: carDoc.id,
          };
        });

        setBestInShowEnabled(settings.bestInShowEnabled === true);
        supportData = {
          entryMap,
          leaderboardLimit: settings.leaderboardLimit || 3,
        };

        await refreshResults();

        if (!cancelled) {
          refreshTimer = window.setInterval(refreshResults, 20000);
        }
      } catch (err) {
        if (!cancelled) {
          reportRefreshFailure(err);
          setLoading(false);
        }
      }
    };

    if (
      !settingsLoading &&
      !roleLoading &&
      categories.length > 0 &&
      eventId &&
      canViewResults
    ) {
      logger.log("✅ Leaderboard results access authorized", {
        resultsAudience: resolvedResultsAudience,
        role: normalizedRole,
        eventRole: normalizedEventRole,
      });

      initializeResults();
    }

    return () => {
      cancelled = true;
      if (refreshTimer) window.clearInterval(refreshTimer);
    };
  }, [
    settingsLoading,
    categories,
    eventId,
    roleLoading,
    canViewResults,
    resolvedResultsAudience,
    normalizedRole,
    normalizedEventRole,
  ]);

  useEffect(() => {
    if (!ready || !eventId) return;

    trackEvent({
      action: GA_EVENTS.LEADERBOARD_VIEWED,
      parameters: {
        event_slug: eventslug,
        event_id: eventId,
        voting_type: votingType,
        aggregation_mode: aggregationMode,
        source_page: window.location.pathname,
      },
    });
  }, [ready, eventId, eventslug, votingType, aggregationMode]);
  
  // ⏳ Wait for everything to load
  // ✅ Wait until event, settings, and role authority are known.
  // Do not include local leaderboard data loading yet because an
  // unauthorized user intentionally never starts the protected fetch.
  if (eventLoading || settingsLoading || roleLoading || !ready) {
    return <FullPageSpinner message="Loading Leaderboard..." />;
  }

  // 🚫 Enforce the configured results audience before protected data loads.
  if (!canViewResults) {
    logger.log("🚫 Results audience denied - redirecting to ballot", {
      resultsAudience: resolvedResultsAudience,
      role: normalizedRole,
      eventRole: normalizedEventRole,
    });

    return <Navigate to={`/${eventslug}/ballot`} replace />;
  }

  // ⏳ Authorized users may wait for the result data fetch to complete.
  if (loading) {
    return <FullPageSpinner message="Loading Leaderboard..." />;
  }

  return (
    <>
    <SeoHead
      {...buildEventPageSeo({
        eventDoc,
        pageKey: "leaderboard",
      })}
    />
    <section id="top-content" className="container-fluid py-5">
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">🏆 Leaderboard</h1>
          <p className="lead text-muted">View the top-voted entries across all categories</p>
        </div>
      </div>
  
      <div className="white-section p-4 rounded shadow-sm bg-white">

        <div className="alert alert-info">
          <strong>Voting Type:</strong>{" "}
          {votingTypeLabel}
        </div>

        <p className="text-muted mb-4">
          {isPointsLeaderboard ? (
            <>
              Scorecards represented below:{" "}
              <strong>{pointsResults?.totalScorecards || 0}</strong>
            </>
          ) : (
            <>
              Votes represented below:{" "}
              <strong>
                {carsWithVotes.reduce(
                  (sum, group) =>
                    sum + group.cars.reduce((a, b) => a + b.count, 0),
                  0
                )}
              </strong>
            </>
          )}
        </p>

        {!isVoteCountLeaderboard &&
        !isRankedLeaderboard &&
        !isPointsLeaderboard && (
            <div className="alert alert-warning">

            Leaderboard calculations for{" "}
            <strong>{aggregationMode}</strong>{" "}
            have not been implemented yet.

          </div>
        )}

        {isRankedLeaderboard && (

          <>
            {rankedChoiceResults.map(group => {

              const winnerCar = group.results?.winnerCar;

              return (
                <div key={group.category} className="mb-5">

                  <h4 className="border-bottom pb-1">
                    {group.category}
                  </h4>

                  {winnerCar ? (

                    <div className="alert alert-success">

                      🏆 Winner:
                      <strong>
                        {" "}
                        #{winnerCar.regcnt} – {winnerCar.description} by {winnerCar.name}
                      </strong>

                    </div>

                  ) : (

                    <div className="alert alert-warning">
                      No winner determined.
                    </div>

                  )}

                  {group.results?.leaderboardPlacements?.length > 0 && (
                    <div className="mt-3">

                      <h6 className="text-muted mb-2">Top 5 Placements</h6>

                      <ol className="list-group list-group-numbered">
                        {group.results.leaderboardPlacements
                          .slice(0, 5)
                          .map((placement) => (
                            <li
                              key={`${placement.place}-${placement.cid}`}
                              className="list-group-item d-flex justify-content-between align-items-center"
                            >
                              <span>
                                #{placement.regcnt} – {placement.description} by {placement.name}
                              </span>

                              <span className="badge bg-primary rounded-pill">
                                {placement.count || 0} vote{placement.count !== 1 ? "s" : ""}
                              </span>

                            </li>
                          ))}
                      </ol>

                    </div>
                  )}

                  <div className="small text-muted">
                    Total Ballots: {group.results?.totalBallots || 0}
                  </div>

                </div>
              );

            })}
          </>

        )}

        {isPointsLeaderboard && (
          <>
            <div className="mb-5">
              <h4 className="border-bottom pb-1">
                🏆 Top Overall Scores
              </h4>

              {pointsResults?.overallTopEntries?.length > 0 ? (

                <ol className="list-group list-group-numbered mt-2">

                  {pointsResults.overallTopEntries.map((entry) => (

                    <li
                      key={`overall-${entry.cid}`}
                      className="list-group-item"
                    >
                      <div className="d-flex justify-content-between align-items-start">

                        <div className="flex-grow-1">
                          #{entry.regcnt} – {entry.description} by {entry.name}
                          <br />
                          <small className="text-muted">
                            {entry.judgeCount} judge{entry.judgeCount !== 1 ? "s" : ""}
                          </small>
                        </div>

                        <div className="text-end">
                          <span className="badge bg-primary rounded-pill">
                            {entry.averageScoreDisplay} / {entry.maxBallotPoints || 100}
                          </span>
                        </div>

                      </div>
                    </li>

                  ))}
                </ol>
              ) : (
                <div className="alert alert-info mt-2">
                  📭 No scorecards have been submitted yet.
                </div>
              )}
            </div>

            {pointsResults?.allEntries?.length > 0 &&
              pointsResults?.criterionResults?.length > 0 && (
              <div className="mb-5">
                <h4 className="border-bottom pb-1">
                  Detailed Judged Results
                </h4>

                <p className="text-muted">
                  Review every evaluated entry and its average score for each
                  scored criterion.
                </p>

                {pointsResults.allEntries.map((entry) => (
                  <div
                    key={`detailed-${entry.cid}`}
                    className="card mb-3"
                  >
                    <div className="card-body">

                      {/* Entry-level judged result summary. */}
                      <div className="d-flex justify-content-between align-items-start mb-3">

                        <div className="flex-grow-1">
                          <h5 className="card-title mb-1">
                            #{entry.placement} - #{entry.regcnt} -{" "}
                            {entry.description} by {entry.name}
                          </h5>

                          <small className="text-muted">
                            {entry.judgeCount} evaluation
                            {entry.judgeCount !== 1 ? "s" : ""}
                          </small>
                        </div>

                        <div className="text-end ms-3">
                          <span className="badge bg-primary rounded-pill">
                            {entry.normalizedAverageScoreDisplay ??
                              entry.averageScoreDisplay} /{" "}
                            {entry.normalizedMaxPoints ??
                              entry.maxBallotPoints ??
                              100}
                          </span>
                        </div>

                      </div>

                      {/* Detailed criterion averages grouped by factor. */}
                      {pointsResults.criterionResults.map((factor) => {

                        const criteriaForEntry =
                          (factor.criteria || [])
                            .map((criterion) => {

                              const criterionEntry =
                                (criterion.entries || []).find(
                                  (resultEntry) =>
                                    String(resultEntry.cid) ===
                                    String(entry.cid)
                                );

                              if (!criterionEntry) {
                                return null;
                              }

                              return {
                                criterion,
                                criterionEntry,
                              };
                            })
                            .filter(Boolean);

                        // Do not render empty factors for this entry.
                        if (criteriaForEntry.length === 0) {
                          return null;
                        }

                        return (
                          <div
                            key={`${entry.cid}-${factor.factorId}`}
                            className="mt-3"
                          >
                            <h6 className="fw-bold mb-2">
                              {factor.factorName}
                            </h6>

                            <ul className="list-group">

                              {criteriaForEntry.map(
                                ({
                                  criterion,
                                  criterionEntry,
                                }) => (
                                  <li
                                    key={
                                      `${entry.cid}-` +
                                      `${factor.factorId}-` +
                                      `${criterion.criterionId}`
                                    }
                                    className="list-group-item d-flex justify-content-between align-items-center"
                                  >
                                    <div>
                                      <div>
                                        {criterion.criterionName}
                                      </div>

                                      <small className="text-muted">
                                        {criterionEntry.judgeCount} evaluation
                                        {criterionEntry.judgeCount !== 1
                                          ? "s"
                                          : ""}
                                      </small>
                                    </div>

                                    <span className="badge bg-secondary rounded-pill">
                                      {criterionEntry.averageScoreDisplay} /{" "}
                                      {criterion.maxPoints}
                                    </span>
                                  </li>
                                )
                              )}

                            </ul>
                          </div>
                        );
                      })}

                    </div>
                  </div>
                ))}
              </div>
            )}
            {pointsResults?.factorLeaderboards?.length > 0 && (
              <div className="mb-5">
                <h4 className="border-bottom pb-1">
                  📊 Top Scores by Scoring Factor
                </h4>

                {pointsResults.factorLeaderboards.map((factor) => (
                  <div key={factor.factorId} className="mb-4">
                    <h5 className="mt-3">
                      {factor.factorName}{" "}
                      <span className="text-muted">
                        / {factor.maxPoints}
                      </span>
                    </h5>

                    {factor.topEntries?.length > 0 ? (
                      <ol className="list-group list-group-numbered mt-2">
                        {factor.topEntries.map((entry) => (

                          <li
                            key={`${factor.factorId}-${entry.cid}`}
                            className="list-group-item"
                          >
                            <div className="d-flex justify-content-between align-items-start">

                              <div className="flex-grow-1">
                                #{entry.regcnt} – {entry.description} by {entry.name}
                                <br />
                                <small className="text-muted">
                                  {entry.judgeCount} judge{entry.judgeCount !== 1 ? "s" : ""}
                                </small>
                              </div>

                              <div className="text-end">
                                <span className="badge bg-secondary rounded-pill">
                                  {entry.averageScoreDisplay} / {entry.maxPoints}
                                </span>
                              </div>

                            </div>
                          </li>

                        ))}
                      </ol>
                    ) : (
                        <div className="text-muted mt-2">
                        No scores submitted for this factor.
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {isVoteCountLeaderboard && (
          <>
          {/* 🏆 Best in Show Section */}
          {showBestInShow && bestInShowCars.length > 0 && (
            <div className="mb-5">
              <h4 className="border-bottom pb-1 text-warning">🏆 Best in Show</h4>
              <ul className="list-group mt-2">
                {bestInShowCars.map(car => (
                  <li
                    key={`bis-${car.regcnt}`}
                    className="list-group-item d-flex justify-content-between align-items-center"
                  >
                    <span>
                      #{car.regcnt} – {car.description} by {car.name}
                    </span>
                    <span className="badge bg-warning text-dark rounded-pill">
                      {car.count} vote{car.count !== 1 ? "s" : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
    
          {carsWithVotes.every(group => group.cars.length === 0) && !bestInShowCars.length && (
            <div className="alert alert-info">📭 No votes have been submitted yet.</div>
          )}
    
          {/* 📋 Category Leaderboards */}
          {carsWithVotes.map(group => (
            <div key={group.category} className="mb-5">
              <h4 className="border-bottom pb-1">{group.category}</h4>
    
              {group.cars.length > 0 ? (
                <ul className="list-group mt-2">
                  {group.cars.map(car => (
                    <li
                      key={`${car.catid}-${car.regcnt}`}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      <span>
                        #{car.regcnt} – {car.description} by {car.name}
                      </span>
                      <span className="badge bg-primary rounded-pill">
                        {car.count} vote{car.count !== 1 ? "s" : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-muted mt-2">
                  No cars have received votes in this category.
                </div>
              )}
            </div>
          ))}
          </>
        )}    
      </div>
    </section>

    <AppPromoMessage />
    </>
  );
};

export default Leaderboard;
